import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { BrandMark } from "../components/ui/brand-mark";
import { Section } from "../components/ui/section";
import { useAuth } from "../auth/AuthContext";

const LAST_UPDATED = "19 June 2026";
const CONTACT_EMAIL = "privacy@tailspend.kinesis.studio";

/**
 * Public privacy policy. Mirrors AboutPage's self-contained shell (its own
 * header + max-w-2xl content column) so it renders without the authenticated
 * AppLayout — it's reachable while signed out, including from the App Store /
 * Play listing. Kept deliberately plain-English and accurate to what the app
 * actually does (sign-in, group data, receipt scanning, ads).
 */
export function PrivacyPage(): ReactNode {
  const { status } = useAuth();
  const home = status === "authenticated" ? "/" : "/welcome";

  return (
    <div className="min-h-screen bg-background text-on-surface">
      <header className="border-b border-outline-variant/40 bg-surface-container-lowest">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to={home} className="flex items-center gap-3">
            <BrandMark />
            <span className="text-headline-md text-primary">Tailspend</span>
          </Link>
          {status === "authenticated" ? (
            <Link to="/">
              <Button variant="ghost" size="sm">
                Back to dashboard
              </Button>
            </Link>
          ) : (
            <Link to="/welcome">
              <Button size="sm">Sign in</Button>
            </Link>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-stack-lg px-6 py-10 sm:px-8">
        <div className="space-y-2">
          <p className="text-label-sm text-on-surface-variant">Legal</p>
          <h1 className="text-headline-lg">Privacy Policy</h1>
          <p className="text-body-md text-on-surface-variant">
            This policy explains what Tailspend collects, why, and the choices
            you have. We keep it short and specific to what the app actually
            does.
          </p>
          <p className="text-label-sm text-on-surface-variant">
            Last updated {LAST_UPDATED}
          </p>
        </div>

        <Section
          title="Who we are"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            Tailspend is an app for tracking and splitting shared expenses
            within groups. This policy covers the Tailspend mobile apps and web
            app.
          </p>
        </Section>

        <Section
          title="Information we collect"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            <strong className="text-on-surface">Account &amp; profile.</strong>{" "}
            When you sign in with Google or Apple we receive a unique account
            identifier, your email address, and your name to create your
            profile. We never see or store your password — authentication is
            handled by your chosen provider.
          </p>
          <p>
            <strong className="text-on-surface">Group activity.</strong> The
            content you create in the app: groups, participants, expenses and
            their splits, payments, recurring expenses, comments, and your
            balances. This is the core data Tailspend exists to keep.
          </p>
          <p>
            <strong className="text-on-surface">Receipt scans.</strong> If you
            use receipt scanning, the photo you capture or upload is sent to our
            servers and processed by text-recognition and AI services to suggest
            expense details. We use the image to produce that draft; you review
            and confirm what gets saved.
          </p>
          <p>
            <strong className="text-on-surface">Advertising.</strong> The mobile
            app offers optional rewarded video ads (via Google AdMob) to earn
            scanning credits. If you choose to watch one, the ad provider may
            access a device advertising identifier and similar device
            information to serve and measure the ad. On iOS we ask for tracking
            permission first; if you decline, you still see non-personalised
            ads.
          </p>
          <p>
            <strong className="text-on-surface">
              Usage &amp; diagnostics.
            </strong>{" "}
            Standard technical data such as app version, device type, and crash
            or error logs, used to keep the app working and diagnose problems.
          </p>
        </Section>

        <Section
          title="How we use your information"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            We use your information to authenticate you, provide and sync your
            groups and expenses, generate balances, power receipt scanning and
            the credit system, serve optional ads, and keep the service secure
            and reliable. We do not sell your personal information.
          </p>
        </Section>

        <Section
          title="Sharing with others"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            Your name, balances, and the expenses you record are visible to the
            other members of the groups you belong to — that is the point of a
            shared ledger. Group owners can share a read-only view of a group
            with people you invite.
          </p>
        </Section>

        <Section
          title="Service providers"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            We rely on a small set of providers to run Tailspend: cloud hosting
            and databases (Amazon Web Services), sign-in (Google and Apple),
            receipt text recognition and AI processing, and advertising (Google
            AdMob). They process data only to provide their service to us.
          </p>
        </Section>

        <Section
          title="Data retention"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            We keep your account and group data for as long as your account is
            active so your shared records stay intact. Receipt images are used
            to create your draft and are not needed long-term. When you delete
            your account we remove your personal profile data; shared records
            you contributed to a group may remain with other members so their
            ledger stays consistent.
          </p>
        </Section>

        <Section
          title="Your choices and rights"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            You can edit your profile in the app, control ad tracking through
            your device settings, and request access to or deletion of your
            personal data by contacting us. Depending on where you live, you may
            have additional rights over your data under local law.
          </p>
        </Section>

        <Section
          title="Children"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            Tailspend is not directed to children and is not intended for use by
            anyone under the age required in their country to consent to such
            services.
          </p>
        </Section>

        <Section
          title="Changes to this policy"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            We may update this policy as the app evolves. When we do, we will
            revise the &ldquo;last updated&rdquo; date above and, for
            significant changes, surface a notice in the app.
          </p>
        </Section>

        <Section
          title="Contact us"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            Questions about this policy or your data? Email us at{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-primary hover:underline"
            >
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>
      </main>
    </div>
  );
}
